import styles from './hero.module.scss';
import React from "react";

import { HeroWave, HERO_WAVE_HOME } from "./waves";

/** Slide del carrusel del hero (Inicio). */
export interface HeroSlide {
  src: string;
  logo?: string;
  slogan?: string;
  /** Duración del slide en ms antes de pasar al siguiente (default 5000). */
  time?: number;
}

export interface HeroProps {
  name?: string;
  classes?: string;
  src?: string;
  /** Home: wordmark + slogan centrados. */
  logo?: string;
  slogan?: string;
  /** Páginas internas: título grande (p. ej. "¿Quiénes somos?"). */
  title?: string;
  /** Subtítulo bajo el título (p. ej. tagline del recetario). */
  tagline?: string;
  align?: "start" | "center" | "end";
  /** Contorno de la máscara (distinto por pantalla). Por defecto, el de Inicio. */
  wave?: HeroWave;
  /**
   * Carrusel (Inicio): si viene con elementos, cicla entre slides con un
   * crossfade, respetando el `time` de cada uno. Tiene prioridad sobre
   * `src`/`logo`/`slogan`.
   */
  slides?: HeroSlide[];
}

interface State {
  index: number;
}

/**
 * Hero curvo de MANGIBONE.
 *
 * La máscara (contorno vectorial del diseño) y la foto van **desacopladas**:
 * - La foto es un `background-size: cover` (nunca se deforma).
 * - El contorno se aplica como `mask-image` con un SVG `preserveAspectRatio="none"`
 *   y `mask-size: 100% 100%`, así el path se **estira al contenedor** y cubre
 *   toda su altura (útil en móvil, donde el hero exige `min-height`).
 *
 * - Home: wordmark MangiBone + slogan (centrado) → `HERO_WAVE_HOME`.
 * - Páginas internas: título (+ tagline opcional) alineado a `align`.
 * - Varios slides: carrusel `fade` (ver `slides`).
 */
export default class Hero extends React.Component<HeroProps, State> {
  static jsClass = "Hero";
  static defaultProps: Partial<HeroProps> = {
    name: "mbHero",
  };

  state: State = { index: 0 };

  private timer?: ReturnType<typeof setTimeout>;

  componentDidMount() {
    this.schedule();
  }

  componentDidUpdate(prev: HeroProps) {
    // El padre memoiza `slides` (referencia estable); sólo reaccionamos a cambios reales.
    if (prev.slides !== this.props.slides) {
      this.setState({ index: 0 }, () => this.schedule());
    }
  }

  componentWillUnmount() {
    this.clearTimer();
  }

  private clearTimer() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
  }

  /** Programa el avance al siguiente slide según el `time` del slide actual. */
  private schedule() {
    this.clearTimer();
    const slides = this.props.slides;
    if (!slides || slides.length < 2) return;
    const time = slides[this.state.index]?.time || 5000;
    this.timer = setTimeout(() => {
      this.setState(
        (s) => ({ index: (s.index + 1) % slides.length }),
        () => this.schedule()
      );
    }, time);
  }

  /**
   * `mask-image` con el contorno del hero. El SVG usa `preserveAspectRatio="none"`
   * para que el path se estire al 100%×100% del contenedor (path deformado,
   * imagen intacta).
   */
  private maskUrl(wave: HeroWave): string {
    const svg =
      `<svg xmlns='http://www.w3.org/2000/svg' viewBox='${wave.viewBox}' ` +
      `preserveAspectRatio='none'><path d='${wave.d}' fill='#fff'/></svg>`;
    return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
  }

  /** Foto (cover) + scrim de legibilidad, ambos recortados por el contorno. */
  private renderMedia(src?: string) {
    return (
      <>
        <div
          className={styles['mb-hero__bg']}
          style={src ? { backgroundImage: `url("${src}")` } : undefined}
          aria-hidden="true"
        />
        <div className={styles['mb-hero__scrim']} aria-hidden="true" />
      </>
    );
  }

  render(): React.ReactNode {
    const { classes, src, logo, slogan, title, tagline, align = "center", slides } = this.props;
    const wave = this.props.wave ?? HERO_WAVE_HOME;
    const [vx, vy, vw, vh] = wave.viewBox.split(" ").map(Number);
    const sectionStyle = {
      "--mb-hero-ratio": `${vw} / ${vh}`,
      "--mb-hero-mask": this.maskUrl(wave),
    } as React.CSSProperties;

    // ── Carrusel (varios slides) ───────────────────────────────────────────
    if (slides && slides.length) {
      return (
        <section
          className={[styles['mb-hero'], styles['mb-hero--carousel'], classes]
            .filter(Boolean)
            .join(' ')}
          style={sectionStyle}
          aria-label={slides[this.state.index]?.slogan || "MANGIBONE"}>
          {slides.map((slide, i) => {
            const active = i === this.state.index;
            return (
              <div
                key={i}
                className={
                  styles['mb-hero__slide'] +
                  (active ? ' ' + styles['mb-hero__slide--active'] : '')
                }
                aria-hidden={!active}>
                {this.renderMedia(slide.src)}
                <div className={styles['mb-hero__content']}>
                  {slide.logo && (
                    <img className={styles['mb-hero__logo']} src={slide.logo} alt="MANGIBONE" />
                  )}
                  {slide.slogan && (
                    <h1 className={styles['mb-hero__slogan']}>{slide.slogan}</h1>
                  )}
                </div>
              </div>
            );
          })}
        </section>
      );
    }

    // ── Slide único ────────────────────────────────────────────────────────
    const alignClass =
      align === "end"
        ? ' ' + styles['mb-hero__page-inner--end']
        : align === "start"
        ? ' ' + styles['mb-hero__page-inner--start']
        : '';

    return (
      <section
        className={styles['mb-hero'] + (classes ? ' ' + classes : '')}
        style={sectionStyle}
        aria-label={title || slogan}>
        {this.renderMedia(src)}

        {title ? (
          <div className={styles['mb-hero__content'] + ' ' + styles['mb-hero__content--page']}>
            <div className={styles['mb-hero__page-inner'] + alignClass}>
              <h1 className={styles['mb-hero__page-title']}>{title}</h1>
              {tagline && <p className={styles['mb-hero__page-tagline']}>{tagline}</p>}
            </div>
          </div>
        ) : (
          <div className={styles['mb-hero__content']}>
            <img className={styles['mb-hero__logo']} src={logo} alt="MANGIBONE" />
            <h1 className={styles['mb-hero__slogan']}>{slogan}</h1>
          </div>
        )}
      </section>
    );
  }
}
